import { DSFlex, type DSGridSpacing, type DSSurfaceColorType } from '@zoe/ds-web'
import type { FC, ReactNode } from 'react'
import styled, { css } from 'styled-components'

import { type Breakpoint, BREAKPOINTS } from '../breakpoints'

export type ColSpanNumber = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12
export type GridSurfaceColour = DSSurfaceColorType

interface GridProps {
  maxWidth?: number
  margin?: DSGridSpacing
  backgroundColor?: GridSurfaceColour
  mobileBackgroundColor?: GridSurfaceColour
  rounded?: boolean
  children: ReactNode
  textAlign?: 'center' | 'left' | 'right'
  backgroundFullWidth?: boolean
  fullWidth?: boolean
  /**
   * Drops the overflow clip. The clip makes this a scroll container, which stops
   * `position: sticky` working anywhere inside it — opt out when a column sticks.
   */
  allowOverflow?: boolean
  dataTestId?: string
}

interface BreakpointProps {
  colSpan?: ColSpanNumber
  offset?: ColSpanNumber
  order?: ColSpanNumber
  gutter?: DSGridSpacing
}

export type BreakConfigProps = Partial<Record<Breakpoint, BreakpointProps>>

// Smallest-first, so the emitted `min-width` media queries cascade correctly.
const BREAKPOINT_ORDER: readonly Breakpoint[] = ['s', 'm', 'l', 'xl']

interface GridBlockProps {
  breakConfig?: BreakConfigProps
  children: ReactNode
  backgroundColor?: GridSurfaceColour
  mobileBackgroundColor?: GridSurfaceColour
  rounded?: boolean
  textAlign?: 'center' | 'left' | 'right'
  padded?: boolean
  dataTestId?: string
}

interface GridConfigBreakpointProps {
  maxColumns: ColSpanNumber
  gutter: DSGridSpacing
  margin: DSGridSpacing
}

type GridConfigProps = Record<Breakpoint, GridConfigBreakpointProps> & {
  maxWidth: number
}

const CONTAINER_MAX_WIDTH = 1536

export const GRID_DEFAULT_CONFIG: GridConfigProps = {
  maxWidth: CONTAINER_MAX_WIDTH,
  s: {
    maxColumns: 2,
    margin: 16,
    gutter: 16,
  },
  m: {
    maxColumns: 6,
    margin: 32,
    gutter: 24,
  },
  l: {
    maxColumns: 12,
    margin: 72,
    gutter: 32,
  },
  xl: {
    maxColumns: 12,
    margin: 72,
    gutter: 32,
  },
}

const StyledGridContainer = styled.div<{
  $backgroundColor: GridSurfaceColour
  $mobileBackgroundColor: GridSurfaceColour
}>`
  ${({ $backgroundColor }) => `background-color: var(--color-surface-${$backgroundColor})`};
  ${({ $mobileBackgroundColor }) =>
    $mobileBackgroundColor &&
    `@media (max-width: ${BREAKPOINTS.m}) { background-color: var(--color-surface-${$mobileBackgroundColor}) }`};
`

const StyledGrid = styled.div<{
  $maxWidth: number
  $backgroundColor: GridSurfaceColour
  $mobileBackgroundColor: GridSurfaceColour
  $rounded: boolean
  $margin: boolean
  $allowOverflow: boolean
}>`
  display: flex;
  flex-wrap: wrap;
  ${({ $allowOverflow }) => !$allowOverflow && 'overflow: hidden;'}
  ${({ $margin }) => $margin && `margin: 0 var(--grid-${GRID_DEFAULT_CONFIG.s.margin});`}

  @media (min-width: ${BREAKPOINTS.m}) {
    ${({ $margin }) => $margin && `margin: 0 var(--grid-${GRID_DEFAULT_CONFIG.m.margin});`}
  }

  @media (min-width: ${BREAKPOINTS.l}) {
    ${({ $margin }) => $margin && `margin: 0 var(--grid-${GRID_DEFAULT_CONFIG.l.margin});`}
  }

  ${({ $backgroundColor }) => `background-color: var(--color-surface-${$backgroundColor})`};
  ${({ $maxWidth }) => `max-width: ${$maxWidth}px`};
  ${({ $rounded }) => $rounded && `border-radius: var(--radius-24)`};
  ${({ $mobileBackgroundColor }) =>
    $mobileBackgroundColor &&
    `@media (max-width: ${BREAKPOINTS.m}) { background-color: var(--color-surface-${$mobileBackgroundColor}) }`};
  width: 100%;
`

const StyledGridBlock = styled.div<{
  $breakConfig?: BreakConfigProps
  $smallerBreakpoint?: 's' | 'm' | 'l' | 'xl'
  $backgroundColor: GridSurfaceColour | undefined
  $mobileBackgroundColor: GridSurfaceColour | undefined
  $rounded: boolean
  $textAlign: 'center' | 'left' | 'right'
  $padded?: boolean
}>`
  ${({ $breakConfig, $smallerBreakpoint, $padded }) => {
    if (!$breakConfig || !$smallerBreakpoint || !$breakConfig[$smallerBreakpoint]) return null
    const { colSpan, offset, order, gutter } = $breakConfig[$smallerBreakpoint]
    const defaults = GRID_DEFAULT_CONFIG[$smallerBreakpoint]
    return css`
      width: ${colSpan ? (colSpan / defaults?.maxColumns) * 100 : 100}%;
      margin-left: ${offset ? (offset / defaults?.maxColumns) * 100 + '%' : '0'};
      order: ${order ?? 'initial'};
      padding: ${$padded ? `0 var(--grid-${gutter ?? defaults?.gutter})` : '0'};
    `
  }}

  ${({ $breakConfig, $padded }) =>
    $breakConfig &&
    BREAKPOINT_ORDER.map((breakpoint) => {
      const config = $breakConfig[breakpoint]
      if (!config) return null

      const { maxColumns, gutter } = GRID_DEFAULT_CONFIG[breakpoint]
      return css`
        @media (min-width: ${BREAKPOINTS[breakpoint]}) {
          width: ${config.colSpan ? (config.colSpan / maxColumns) * 100 : 100}%;
          margin-left: ${config.offset ? `${(config.offset / maxColumns) * 100}%` : '0'};
          order: ${config.order ? config.order : 'initial'};
          padding: ${$padded ? `0 var(--grid-${config.gutter ?? gutter})` : '0'};
        }
      `
    })}
  ${({ $backgroundColor }) => `background-color: var(--color-surface-${$backgroundColor})`};
  ${({ $mobileBackgroundColor }) =>
    $mobileBackgroundColor &&
    `@media (max-width: ${BREAKPOINTS.m}) { background-color: var(--color-surface-${$mobileBackgroundColor}) }`};
  ${({ $rounded }) => $rounded && `border-radius: var(--radius-24)`};
  ${({ $textAlign }) => `text-align: ${$textAlign};`};
`

export const DSGrid = ({
  children,
  maxWidth,
  backgroundColor = 'neutral',
  mobileBackgroundColor = 'neutral',
  rounded = false,
  backgroundFullWidth = false,
  fullWidth = false,
  allowOverflow = false,
  dataTestId,
}: GridProps) => {
  const gridContent = (
    <DSFlex direction="row" justifyContent="center" alignItems="center">
      <StyledGrid
        $maxWidth={maxWidth ?? GRID_DEFAULT_CONFIG.maxWidth}
        $backgroundColor={backgroundColor}
        $mobileBackgroundColor={mobileBackgroundColor}
        $rounded={rounded}
        $margin={!fullWidth}
        $allowOverflow={allowOverflow}
      >
        {children}
      </StyledGrid>
    </DSFlex>
  )

  return backgroundFullWidth ? (
    <StyledGridContainer
      $backgroundColor={backgroundColor}
      $mobileBackgroundColor={mobileBackgroundColor}
      {...(dataTestId && { 'data-testid': dataTestId, id: dataTestId })}
    >
      {gridContent}
    </StyledGridContainer>
  ) : (
    <div {...(dataTestId && { 'data-testid': dataTestId, id: dataTestId })}>{gridContent}</div>
  )
}

export const DSGridBlock: FC<GridBlockProps> = ({
  children,
  breakConfig,
  backgroundColor,
  mobileBackgroundColor,
  rounded = false,
  textAlign = 'left',
  padded = false,
  dataTestId,
}) => {
  if (breakConfig?.s?.colSpan && breakConfig?.s?.offset) {
    if (breakConfig.s?.colSpan + breakConfig.s?.offset > GRID_DEFAULT_CONFIG.s.maxColumns) {
      console.warn('Bad grid')
    }
  }

  const defaultBreakConfig: BreakConfigProps = {
    s: { colSpan: 2 },
    m: { colSpan: 6 },
    l: { colSpan: 12 },
    xl: { colSpan: 12 },
  }
  const smallerBreakpoint = breakConfig?.s
    ? 's'
    : breakConfig?.m
      ? 'm'
      : breakConfig?.l
        ? 'l'
        : breakConfig?.xl
          ? 'xl'
          : 's'
  return (
    <StyledGridBlock
      $breakConfig={{ ...defaultBreakConfig, ...breakConfig }}
      $smallerBreakpoint={smallerBreakpoint}
      $backgroundColor={backgroundColor}
      $mobileBackgroundColor={mobileBackgroundColor}
      $rounded={rounded}
      $textAlign={textAlign}
      $padded={padded}
      {...(dataTestId && { 'data-testid': dataTestId, id: dataTestId })}
    >
      {children}
    </StyledGridBlock>
  )
}
